import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import React, { useState } from 'react';
import CloseIcon from '../icons/CloseIcon';
import { useTranslation } from 'react-i18next';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { isValidEmail } from '@pok/utils';
import { useLoading } from '@pok/weblib/context/loading-context.jsx';
import { submitLead } from '@pok/viewer-api-sdk';
import { trackActivity } from '../../utils/custom-track-events.js';
import { LegacyPageTrackAction as PageTrackAction, LegacyTrackCategory as TrackCategory } from '@pok/viewer-api-sdk';

export default function LeadDialog({ open, onClose, widget, certificate }) {
    const { t } = useTranslation();
    const [inputs, setInputs] = useState({ email: '', firstname: '', lastname: '' });
    const { showLoading, showLoadingSuccess, hideLoading } = useLoading();
    const handleSubmit = () => {
        showLoading();
        submitLead(import.meta.env.VITE_VIEWER_API_ROOT, {
            email: inputs.email,
            firstname: inputs.firstname,
            lastname: inputs.lastname,
            certificateId: certificate.id,
            widget: { positionX: widget.xPosition, positionY: widget.yPosition },
        })
            .then(() =>
                trackActivity(
                    TrackCategory.PAGE,
                    PageTrackAction.LEAD,
                    certificate.id,
                    certificate.minter,
                    certificate.customPage,
                    {
                        x: widget.xPosition,
                        y: widget.yPosition,
                        version: widget.version,
                    },
                ),
            )
            .then(() => {
                showLoadingSuccess();
                onClose();
            })
            .finally(() => hideLoading());
    };
    const checkEnable = () =>
        inputs.firstname.trim().length > 1 && inputs.lastname.trim().length > 1 && isValidEmail(inputs.email);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            sx={{
                '& .MuiPaper-root': {
                    padding: 2,
                    borderRadius: 4,
                    whiteSpace: 'pre-line',
                    width: { xs: 300, sm: 400 },
                },
            }}
        >
            <DialogTitle>
                <Typography variant="h3" component={'span'}>
                    {t('pages.lead_title')}
                </Typography>
                <span onClick={onClose} style={{ position: 'absolute', right: 12, top: 12, cursor: 'pointer' }}>
                    <CloseIcon />
                </span>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Typography variant="body2" pb={2}>
                        {t('pages.lead_subtitle')}
                    </Typography>

                    <TextField
                        id="firstname"
                        label={t('pages.lead_first_name')}
                        value={inputs.firstname}
                        onChange={(e) => setInputs({ ...inputs, firstname: e.target.value })}
                    />
                    <TextField
                        id="lastname"
                        label={t('pages.lead_last_name')}
                        value={inputs.lastname}
                        onChange={(e) => setInputs({ ...inputs, lastname: e.target.value })}
                    />
                    <TextField
                        id="email"
                        label={t('pages.lead_email')}
                        value={inputs.email}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && checkEnable()) handleSubmit();
                        }}
                        onChange={(e) => setInputs({ ...inputs, email: e.target.value })}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ alignSelf: 'center', marginBottom: '8px' }}>
                <Button variant="contained" onClick={handleSubmit} disabled={!checkEnable()} sx={{ px: 4, py: 1 }}>
                    {t('pages.lead_submit')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
